import { Box } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { ErrorMessage, Formik, Form as FormikForm, FormikProps } from "formik";
import { FormikHelpers } from "formik/dist/types";
import { TFunction } from "i18next";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { DatePicker } from "rsuite";
import { number, NumberSchema, object, string } from "yup";

import localStyle from "./form.scss";
import Button from "components/button/Button";
import { CalendarIcon } from "components/icons/CalendarIcon";
import Info from "components/icons/Info";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import {
    createExpirationDateSchema,
    createStartDateSchema,
    resolveExpirationDateToSubmit,
    resolveMinimumStartDate,
    resolveStartDateToSubmit,
} from "components/licenses/assign-bms-keys/licenseKeyDates";
import {
    MAX_IDENTIFIER_LENGTH,
    MAX_KEY_LENGTH,
} from "components/licenses/bulk-import-license-key/ImportLicenseKeyDialog";
import { createLicenseTypes, OEM_LICENSE_ID } from "components/licenses/common";
import { createLicense } from "components/licenses/delivery-history/AddLicenseDeliveryView";
import { createDefaultSelectedLicense, License } from "components/licenses/delivery-history/DeliveryFormContent";
import LicenseKeySelectionForm from "components/licenses/delivery/LicenseKeySelectionForm";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import Tooltip from "components/tooltip/Tooltip";
import { Key, LicenseData } from "domain/licenses";
import { LicenseKeyAssignment, licenseService } from "services/licenses/LicenseService";
import { Action, Category, Event, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { StoreState } from "store";
import buttonStyle from "styles/buttons.scss";
import buttons from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import form from "styles/form.scss";
import { createDateLabel, daysAfterToday, formatFormDate, formatIsoDate, toStartOfCalendarDay } from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

enum Phase {
    FAILED,
    FETCH_AVAILABLE_LICENSES_FAILED,
    LOADING_AVAILABLE_LICENSES,
    SENDING,
    USER,
}

interface State {
    phase: Phase;
    formValues: LicenseKeyAssignment;
}

export interface SummaryLabelParameter {
    initialRender: boolean;
    availableLicenses: number;
    expiration: string;
    licenseName: string;
}

export interface Props {
    onSuccess: () => void;
    onFetchLicensesFailure: () => void;
    licenseKeyData?: Key;
    isEdit: boolean;
    fromBms: boolean;
    availableLicenses: LicenseData[];
}

type InputType = "text" | "date" | "number";

interface FormSection {
    name: InputName;
    localizationId: string;
    optional: boolean;

    createError(): JSX.Element | null;

    createField(formikProps: FormikProps<LicenseKeyAssignment>, t: TFunction): JSX.Element;
}

type InputName = keyof LicenseKeyAssignment;

class FormSectionInput implements FormSection {
    name: InputName;
    localizationId: string;
    inputTestId: string;
    errorTestId: string;
    inputType: InputType;
    autoFocus: boolean;
    optional: boolean;
    tooltipText?: string;

    constructor(
        name: InputName,
        localizationId: string,
        inputTestId: string,
        errorTestId: string,
        inputType: InputType,
        {
            autoFocus = false,
            optional = false,
        }: {
            autoFocus?: boolean;
            optional?: boolean;
        } = {},
        tooltipText?: string
    ) {
        this.name = name;
        this.localizationId = localizationId;
        this.inputTestId = inputTestId;
        this.errorTestId = errorTestId;
        this.inputType = inputType;
        this.autoFocus = autoFocus;
        this.optional = optional;
        this.tooltipText = tooltipText;
    }

    createField(formikProps: FormikProps<LicenseKeyAssignment>, t: TFunction): JSX.Element {
        let tooltip = null;
        const theme = useTheme();
        if (this.tooltipText != null) {
            tooltip = (
                <Tooltip content={t(this.tooltipText)}>
                    <div className={localStyle.info} tabIndex={0}>
                        <Info borderColor={theme.contentBackgroundColor} color={theme.iconFillColor} />
                    </div>
                </Tooltip>
            );
        }
        return (
            <Box verticalAlignment="Center">
                <input
                    id={this.name}
                    data-testid={this.inputTestId}
                    onBlur={(e) => {
                        formikProps.handleBlur(e);
                    }}
                    onChange={(e) => {
                        formikProps.handleChange(e);
                        sendUsageStatisticsHit(this.name);
                    }}
                    className={classNames(formStyle.input, formStyle.fixedWidthInput, {
                        [formStyle.inputError]: formikProps.errors[this.name],
                    })}
                    type={this.inputType}
                    value={this.name !== "enabled" ? formikProps.values[this.name] : ""}
                />
                {tooltip}
            </Box>
        );
    }

    createError(): JSX.Element {
        return (
            <div className={formStyle.error} data-testid={this.errorTestId}>
                <ErrorMessage name={this.name} />
            </div>
        );
    }
}

class CustomDatePicker implements FormSection {
    name: InputName;
    localizationId: string;
    inputTestId: string;
    errorTestId: string;
    optional: boolean;
    summaryLabelParameter: SummaryLabelParameter;
    minimumStartDate: Date;

    constructor(
        name: InputName,
        localizationId: string,
        inputTestId: string,
        errorTestId: string,
        optional: boolean,
        summaryLabelParameter: SummaryLabelParameter,
        minimumStartDate: Date
    ) {
        this.name = name;
        this.localizationId = localizationId;
        this.inputTestId = inputTestId;
        this.errorTestId = errorTestId;
        this.optional = optional;
        this.summaryLabelParameter = summaryLabelParameter;
        this.minimumStartDate = minimumStartDate;
    }

    createField(formikProps: FormikProps<LicenseKeyAssignment>, t: TFunction): JSX.Element {
        const theme = useTheme();
        const validDateFrom = toStartOfCalendarDay(
            this.name === "startDate" ? formikProps.values.startDate : formikProps.values.expirationDate
        );
        const [selectedDate, setSelectedDate] = useState<Date>(validDateFrom);
        // The calendar must offer exactly the days the validation schema accepts. Otherwise the user can select a day
        // that is only rejected when the form is submitted.
        const earliestDate =
            this.name === "startDate" ? this.minimumStartDate : toStartOfCalendarDay(formikProps.values.startDate);
        const latestDate = toStartOfCalendarDay(this.summaryLabelParameter.expiration);
        const isDateDisabled = (date: Date) => {
            const day = toStartOfCalendarDay(date);
            return day < earliestDate || day > latestDate;
        };
        const handleClean = () => {
            setSelectedDate(validDateFrom);
            formikProps.setFieldValue(this.name, formatIsoDate(validDateFrom));
        };
        React.useEffect(() => {
            setSelectedDate(validDateFrom);
            formikProps.setFieldValue(this.name, formatIsoDate(validDateFrom));
        }, [validDateFrom.getTime()]);
        React.useEffect(() => {
            if (formikProps.errors[this.name]) {
                formikProps.setFieldTouched(this.name, true, false);
            }
        }, [formikProps.errors[this.name]]);
        return (
            <>
                <DatePicker
                    id={this.name}
                    caretAs={(colorProps) => <CalendarIcon {...colorProps} color={theme.iconFillColor} />}
                    shouldDisableDate={isDateDisabled}
                    ranges={[
                        {
                            label: "Today",
                            value: new Date(),
                            closeOverlay: true,
                        },
                    ]}
                    title={t("AltText.calendar")}
                    placeholder={createDateLabel(validDateFrom)}
                    onSelect={(date: Date) => {
                        setSelectedDate(toStartOfCalendarDay(date));
                        formikProps.setFieldValue(this.name, formatIsoDate(date));
                    }}
                    onClean={handleClean}
                    placement={"bottom"}
                    className={form.dateRange}
                    value={new Date(selectedDate)}
                    data-testid={this.inputTestId}
                />
            </>
        );
    }

    createError(): JSX.Element {
        return (
            <div className={formStyle.error} data-testid={this.errorTestId}>
                <ErrorMessage name={this.name} />
            </div>
        );
    }
}

class FormSectionLabel implements FormSection {
    name: InputName;
    localizationId: string;
    optional: boolean;
    testId: string;

    constructor(name: InputName, localizationId: string, testId: string) {
        this.name = name;
        this.localizationId = localizationId;
        this.optional = false;
        this.testId = testId;
    }

    createError(): JSX.Element | null {
        return null;
    }

    createField(formikProps: FormikProps<LicenseKeyAssignment>): JSX.Element {
        return (
            <span className={formStyle.fixedWidthInput} data-testid={this.testId}>
                {formikProps.values[this.name]}
            </span>
        );
    }
}

function createFormSection(
    t: TFunction,
    formSection: FormSection,
    key: string,
    formikProps: FormikProps<LicenseKeyAssignment>
) {
    let optionalElement = null;
    if (formSection.optional) {
        optionalElement = <span className={formStyle.optional}>{t("Common.optional")}</span>;
    }
    return (
        <div key={key} className={formStyle.formFields}>
            <div className={form.formFieldsColumnDirection}>
                {optionalElement}
                <label
                    htmlFor={formSection.name}
                    className={classNames(formStyle.label, {
                        [formStyle.inputError]: formikProps.errors[formSection.name],
                    })}
                >
                    {t(formSection.localizationId)}
                </label>
            </div>
            {formSection.createField(formikProps, t)}
            {formSection.createError()}
        </div>
    );
}

const sendUsageStatisticsHit = (labelName: string) => {
    const event: Event = {
        category: Category.LICENSE,
        action: "",
    };
    switch (labelName) {
        case "keyCount":
            event.action = Action.CHANGE_NUMBER_OF_LICENSE_KEYS;
            break;
        case "identifier":
            event.action = Action.CHANGE_LICENSE_KEY_IDENTIFIER;
            break;
        case "hours":
            event.action = Action.CHANGE_LICENSE_KEY_USAGE_HOURS;
            break;
        case "startDate":
            event.action = Action.CHANGE_LICENSE_KEY_START_DATE;
            break;
        case "expirationDate":
            event.action = Action.CHANGE_LICENSE_KEY_END_DATE;
            break;
        case "assignCount":
            event.action = Action.CHANGE_LICENSE_KEY_ASSIGNED_COUNT;
            break;
        default:
            return;
    }
    usageStatisticsService.sendEvent(event);
};

const connector = connect((state: StoreState) => ({
    hasBmsValidationLicenses: state.licensesReducer.hasBmsValidationLicenses,
    hasBmsBbtiLicenses: state.licensesReducer.hasBmsBbtiLicenses,
    hasBmsInsuranceLicenses: state.licensesReducer.hasBmsInsuranceLicenses,
    hasBmsNtfLicenses: state.licensesReducer.hasBmsNtfLicenses,
    hasBmsLeaseLicenses: state.licensesReducer.hasBmsLeaseLicenses,
    hasBmsAllLicenses: state.licensesReducer.hasBmsAllLicenses,
    hasUsdkLicenses: state.licensesReducer.hasUsdkLicenses,
}));

function ManageLicenseKeyView(props: Props & ConnectedProps<typeof connector>): JSX.Element {
    const { t } = useTranslation();
    const [availableLicenses, setAvailableLicenses] = React.useState<License[]>();
    const [selectedLicense, setSelectedLicense] = React.useState<License>(createDefaultSelectedLicense(t));
    const [licenseSelectionErrors, setLicenseSelectionErrors] = React.useState<boolean>(false);
    const BMS_VALIDATION_KEY = "bms-validation";
    const BMS_BBTI_KEY = "bms-bbti";
    const BMS_INSURANCE_KEY = "bms-insurance";
    const BMS_NTF_KEY = "bms-ntf";
    const BMS_LEASE_KEY = "bms-lease";
    const BMS_ALL_KEY = "bms-all";
    const USDK_KEY = "usdk";
    const DEFAULT_BMS_KEY = props.hasBmsValidationLicenses
        ? BMS_VALIDATION_KEY
        : props.hasBmsBbtiLicenses
          ? BMS_BBTI_KEY
          : props.hasUsdkLicenses
            ? USDK_KEY
            : props.hasBmsLeaseLicenses
              ? BMS_LEASE_KEY
              : props.hasBmsAllLicenses
                ? BMS_ALL_KEY
                : props.hasBmsNtfLicenses
                  ? BMS_NTF_KEY
                  : BMS_INSURANCE_KEY;
    const [licenseType, setLicenseType] = React.useState<string>(
        props.licenseKeyData?.licenseType ?? (props.fromBms ? DEFAULT_BMS_KEY : OEM_LICENSE_ID)
    );
    const [summaryLabelParameter, setSummaryLabelParameter] = React.useState<SummaryLabelParameter>({
        initialRender: true,
        availableLicenses: props.isEdit ? (props.licenseKeyData?.remaining ?? 0) : 0,
        expiration: props.isEdit
            ? (props.licenseKeyData?.expirationDate ?? "1970-01-01T00:00:00Z")
            : (selectedLicense.expirationDate as string),
        licenseName: props.licenseKeyData?.licenseType ?? (props.fromBms ? DEFAULT_BMS_KEY : OEM_LICENSE_ID),
    });
    const minimumStartDate = resolveMinimumStartDate(
        new Date(),
        props.isEdit ? props.licenseKeyData?.startDate : undefined
    );

    const FORM_SECTIONS = [
        props.isEdit
            ? new FormSectionLabel(
                  "keyCount",
                  "Licenses.bms.assignDialog.numberOfKeys",
                  testIds.workArea.license.bms.assignKeysDialog.numberOfKeysInput.itself
              )
            : new FormSectionInput(
                  "keyCount",
                  "Licenses.bms.assignDialog.numberOfKeys",
                  testIds.workArea.license.bms.assignKeysDialog.numberOfKeysInput.itself,
                  testIds.workArea.license.bms.assignKeysDialog.numberOfKeysInput.errorLabel,
                  "number",
                  { autoFocus: true }
              ),
        new FormSectionLabel(
            "association",
            "Licenses.bms.assignDialog.association",
            testIds.workArea.license.bms.assignKeysDialog.associationLabel
        ),
        ...(props.fromBms
            ? []
            : [
                  props.isEdit
                      ? new FormSectionLabel(
                            "licenseKey",
                            "Licenses.bms.assignDialog.licenseKey",
                            testIds.workArea.license.serialNumber.assignKeysView.licenseKeyInput.itself
                        )
                      : new FormSectionInput(
                            "licenseKey",
                            "Licenses.bms.assignDialog.licenseKey",
                            testIds.workArea.license.serialNumber.assignKeysView.licenseKeyInput.itself,
                            testIds.workArea.license.serialNumber.assignKeysView.licenseKeyInput.errorLabel,
                            "text",
                            { autoFocus: !props.fromBms }
                        ),
              ]),
        new FormSectionInput(
            "identifier",
            "Licenses.bms.assignDialog.identifier",
            props.fromBms
                ? testIds.workArea.license.bms.assignKeysDialog.identifierInput.itself
                : testIds.workArea.license.serialNumber.assignKeysView.identifierInput.itself,
            props.fromBms
                ? testIds.workArea.license.bms.assignKeysDialog.identifierInput.errorLabel
                : testIds.workArea.license.serialNumber.assignKeysView.identifierInput.errorLabel,
            "text",
            { optional: true },
            "Licenses.bms.assignDialog.identifierTooltip"
        ),
        ...(licenseType === OEM_LICENSE_ID
            ? []
            : [
                  new FormSectionInput(
                      "hours",
                      "Licenses.bms.assignDialog.usageHours",
                      testIds.workArea.license.bms.assignKeysDialog.hoursInput.itself,
                      testIds.workArea.license.bms.assignKeysDialog.hoursInput.errorLabel,
                      "number"
                  ),
              ]),
        new CustomDatePicker(
            "startDate",
            "Licenses.bms.assignDialog.startDate",
            props.fromBms
                ? testIds.workArea.license.bms.assignKeysDialog.startDateInput.itself
                : testIds.workArea.license.serialNumber.assignKeysView.startDateInput.itself,
            props.fromBms
                ? testIds.workArea.license.bms.assignKeysDialog.startDateInput.errorLabel
                : testIds.workArea.license.serialNumber.assignKeysView.startDateInput.errorLabel,
            false,
            summaryLabelParameter,
            minimumStartDate
        ),
        new CustomDatePicker(
            "expirationDate",
            "Licenses.bms.assignDialog.expirationDate",
            props.fromBms
                ? testIds.workArea.license.bms.assignKeysDialog.expirationDateInput.itself
                : testIds.workArea.license.serialNumber.assignKeysView.expirationDateInput.itself,
            props.fromBms
                ? testIds.workArea.license.bms.assignKeysDialog.expirationDateInput.errorLabel
                : testIds.workArea.license.serialNumber.assignKeysView.expirationDateInput.errorLabel,
            false,
            summaryLabelParameter,
            minimumStartDate
        ),
    ];

    const manageFormSections = () => {
        const formSections = [...FORM_SECTIONS];
        return formSections.filter((input) => {
            if (
                !props.fromBms &&
                (input?.name === "keyCount" ||
                    input?.name === "hours" ||
                    input?.name === "licenseType" ||
                    input?.name === "association")
            ) {
                return false;
            } else if (props.fromBms && input?.name === "licenseKey") {
                return false;
            }
            return true;
        });
    };

    const DEFAULT_FORM_VALUES = {
        licenseKey: "",
        keyCount: 1,
        association: t("Licenses.bms.assignDialog.associationMultiple"),
        identifier: undefined,
        hours: 336,
        licenseType: props.fromBms ? DEFAULT_BMS_KEY : OEM_LICENSE_ID,
        available: summaryLabelParameter.availableLicenses,
        assignCount: 1,
        startDate: daysAfterToday(0),
        expirationDate: formatFormDate(selectedLicense.expirationDate as string),
    };

    const EDIT_DEFAULT_FORM_VALUES = {
        licenseKey: props.licenseKeyData?.licenseKey,
        keyCount: 1,
        association: t("Licenses.bms.assignDialog.associationMultiple"),
        identifier: props.licenseKeyData?.identifier ?? "",
        hours: props.licenseKeyData?.usageHoursLimit ?? 1,
        licenseType: props.licenseKeyData?.licenseType ?? (props.fromBms ? DEFAULT_BMS_KEY : OEM_LICENSE_ID),
        available: summaryLabelParameter.availableLicenses,
        assignCount: 0,
        startDate: formatFormDate(props.licenseKeyData?.startDate ?? daysAfterToday(0)),
        expirationDate: props.licenseKeyData?.expirationDate
            ? formatFormDate(props.licenseKeyData.expirationDate)
            : daysAfterToday(1),
    };

    const [state, setState] = React.useState<State>({
        phase: Phase.LOADING_AVAILABLE_LICENSES,
        formValues: props?.isEdit
            ? { ...EDIT_DEFAULT_FORM_VALUES, licenseKey: EDIT_DEFAULT_FORM_VALUES.licenseKey! }
            : DEFAULT_FORM_VALUES,
    });

    const [resultVisible, setResultVisible] = React.useState<boolean>(false);
    const [successResultVisible, setSuccessResultVisible] = React.useState<boolean>(false);

    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const formikRef = React.useRef<FormikProps<LicenseKeyAssignment>>(null);

    const fetchAvailableLicenses = (licenseType: string) => {
        const license = props.availableLicenses.find((license) => license.type === licenseType);
        if (!license) {
            if (!(props.isEdit && props.licenseKeyData)) {
                setResultVisible(true);
                setState({
                    ...state,
                    phase: Phase.FETCH_AVAILABLE_LICENSES_FAILED,
                });
            }
            return () => {
                abortControllers.forEach((abortController) => abortController.abort());
            };
        }
        setSummaryLabelParameter({
            initialRender: false,
            availableLicenses: license.available,
            expiration: license.expirationDate,
            licenseName:
                createLicenseTypes(true).find((license) => license.productId == licenseType)?.productName ?? "",
        });
        return license;
    };

    function deriveOwnLicense() {
        const allLicenses: License[] = [];
        let foundLicenses;
        const enabledLicenseTypes = [];
        if (props.fromBms) {
            props.hasBmsValidationLicenses && enabledLicenseTypes.push(BMS_VALIDATION_KEY);
            props.hasBmsBbtiLicenses && enabledLicenseTypes.push(BMS_BBTI_KEY);
            props.hasBmsInsuranceLicenses && enabledLicenseTypes.push(BMS_INSURANCE_KEY);
            props.hasBmsNtfLicenses && enabledLicenseTypes.push(BMS_NTF_KEY);
            props.hasBmsLeaseLicenses && enabledLicenseTypes.push(BMS_LEASE_KEY);
            props.hasBmsAllLicenses && enabledLicenseTypes.push(BMS_ALL_KEY);
            props.hasUsdkLicenses && enabledLicenseTypes.push(USDK_KEY);
        } else {
            enabledLicenseTypes.push(OEM_LICENSE_ID);
        }

        enabledLicenseTypes.forEach((each) => {
            const matchingLicense = props.availableLicenses.find((license) => license.type === each);

            if (!matchingLicense) {
                return;
            }

            const licenseToAdd =
                props.licenseKeyData?.licenseType === each
                    ? createLicense(
                          props.licenseKeyData.licenseType,
                          props.licenseKeyData.remaining,
                          matchingLicense.license,
                          props.licenseKeyData.expirationDate,
                          allLicenses.length,
                          matchingLicense.available,
                          0,
                          matchingLicense.expirationDate
                      )
                    : createLicense(
                          matchingLicense.type,
                          0,
                          matchingLicense.license,
                          matchingLicense.expirationDate,
                          allLicenses.length,
                          matchingLicense.available,
                          1,
                          matchingLicense.expirationDate
                      );
            allLicenses.push(licenseToAdd);
            if (each == (props.licenseKeyData?.licenseType ?? (props.fromBms ? DEFAULT_BMS_KEY : OEM_LICENSE_ID))) {
                foundLicenses = licenseToAdd;
            }
        });
        return { allLicenses: allLicenses.sort((a, b) => (a.productName < b.productName ? -1 : 1)), foundLicenses };
    }

    React.useEffect(() => {
        fetchAvailableLicenses(props.licenseKeyData?.licenseType || (props.fromBms ? DEFAULT_BMS_KEY : OEM_LICENSE_ID));
        const computedLicense = deriveOwnLicense();
        setAvailableLicenses(computedLicense.allLicenses);
        if (computedLicense.foundLicenses) {
            setSelectedLicense(computedLicense.foundLicenses);
        } else {
            setResultVisible(true);
            setState({
                ...state,
                phase: Phase.FETCH_AVAILABLE_LICENSES_FAILED,
            });
            return () => {
                abortControllers.forEach((abortController) => abortController.abort());
            };
        }
        if (props.isEdit && !props.licenseKeyData?.licenseType) {
            setResultVisible(true);
            setState({
                ...state,
                phase: Phase.FETCH_AVAILABLE_LICENSES_FAILED,
            });
            return () => {
                abortControllers.forEach((abortController) => abortController.abort());
            };
        }

        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [props.availableLicenses]);

    React.useEffect(() => {
        setSummaryLabelParameter({
            initialRender: false,
            availableLicenses: selectedLicense.parentAvailableAmount,
            expiration: selectedLicense.parentLicenseExpirationDate || "",
            licenseName:
                createLicenseTypes(true).find((license) => license.productId == selectedLicense.licenseType)
                    ?.productName ?? "",
        });
        if (!summaryLabelParameter.initialRender) {
            setState({
                phase: Phase.USER,
                formValues: {
                    ...state.formValues,
                    licenseType: selectedLicense.licenseType,
                    available: selectedLicense.parentAvailableAmount,
                },
            });
        }
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [selectedLicense]);

    function submit(values: LicenseKeyAssignment, formikHelpers: FormikHelpers<LicenseKeyAssignment>) {
        values.startDate = resolveStartDateToSubmit(values.startDate, props.licenseKeyData?.startDate);
        values.expirationDate = resolveExpirationDateToSubmit(values.expirationDate);

        setResultVisible(true);
        setState({ phase: Phase.SENDING, formValues: values });
        if (props.fromBms) {
            values.licenseKey = undefined;
            values.enabled = undefined;
        } else {
            values.keyCount = undefined;
            values.hours = undefined;
            values.association = undefined;
        }
        props.isEdit
            ? licenseService
                  .updateLicenseKey(props.licenseKeyData?.licenseKey ?? "", values)
                  .then(() => {
                      setResultVisible(false);
                      setSuccessResultVisible(true);
                  })
                  .catch(() => {
                      setResultVisible(true);
                      setState((previous) => ({ phase: Phase.FAILED, formValues: previous.formValues }));
                  })
            : props.fromBms
              ? assignKeys(values)
              : assignOemKeys(values, formikHelpers);
    }

    function assignKeys(values: LicenseKeyAssignment) {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        licenseService
            .assignLicenseKey(values)
            .then(() => {
                setResultVisible(false);
                setSuccessResultVisible(true);
            })
            .catch(() => {
                setResultVisible(true);
                setState((previous) => ({ phase: Phase.FAILED, formValues: previous.formValues }));
            });
    }

    function assignOemKeys(values: LicenseKeyAssignment, formikHelpers?: FormikHelpers<LicenseKeyAssignment>) {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        licenseService
            .checkLicenseKeyAvailability(abortController, values.licenseKey)
            .then((response) => {
                if (response.licenseKeys.available) {
                    assignKeys(values);
                } else {
                    setResultVisible(false);
                    formikHelpers?.setFieldError(
                        "licenseKey",
                        t("Licenses.bms.assignDialog.licenseKeyUnavailableError")
                    );
                }
            })
            .catch(() => {
                setResultVisible(true);
                setState((previous) => ({ phase: Phase.FAILED, formValues: previous.formValues }));
            });
    }

    const hideModel = () => {
        if (state.phase === Phase.FETCH_AVAILABLE_LICENSES_FAILED) {
            props.onFetchLicensesFailure();
        }
        if (state.phase === Phase.FAILED) {
            setState((previous) => ({
                phase: Phase.USER,
                formValues: previous.formValues,
            }));
        }
        setResultVisible(false);
    };

    const hideResult = () => {
        props.onSuccess();
        setSuccessResultVisible(false);
    };

    function createModalBody() {
        return (
            <>
                {state.phase === Phase.SENDING && <LoadingIndicator />}
                {state.phase === Phase.FETCH_AVAILABLE_LICENSES_FAILED && (
                    <div>
                        <div className={formStyle.submitError}>
                            {t("Licenses.bms.assignDialog.fetchLicensesFailureMessage")}
                        </div>
                        <div className={formStyle.buttonContainer}>
                            <button
                                className={buttonStyle.primaryButtonWithoutIcon}
                                type="button"
                                data-testid={testIds.common.dialog.closeButton}
                                onClick={hideModel}
                            >
                                {t("Common.close")}
                            </button>
                        </div>
                    </div>
                )}
                {state.phase === Phase.FAILED && (
                    <div>
                        <div className={formStyle.submitError}>{t("Licenses.bms.assignDialog.failureMessage")}</div>
                        <div className={formStyle.buttonContainer}>
                            <button
                                className={buttonStyle.primaryButtonWithoutIcon}
                                type="button"
                                data-testid={testIds.common.dialog.closeButton}
                                onClick={hideModel}
                            >
                                {t("Common.close")}
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    }

    function getMinimumAssignCount() {
        return selectedLicense.available > 0 ? -selectedLicense.available : 0;
    }

    const schema = React.useMemo(() => {
        return object().shape({
            keyCount: props.fromBms
                ? number()
                      .min(1, (params) => t("Licenses.bms.assignDialog.numberOfKeysMinError", { min: params.min }))
                      .max(300, (params) => t("Licenses.bms.assignDialog.numberOfKeysMaxError", { max: params.max }))
                      .required(t("Licenses.bms.assignDialog.numberOfKeysRequiredError"))
                : number(),
            identifier: string().max(MAX_IDENTIFIER_LENGTH, (params) =>
                t("Licenses.bms.assignDialog.identifierMaxError", { max: params.max })
            ),
            licenseKey: !props.fromBms
                ? string()
                      .max(MAX_KEY_LENGTH, (params) =>
                          t("Licenses.bms.assignDialog.licenseKeyMaxError", { max: params.max })
                      )
                      .test("no-spaces", t("Licenses.bms.assignDialog.licenseKeyNoSpacesError"), (value) => {
                          return !value || !value.includes(" ");
                      })
                      .matches(/^[A-Za-z0-9-]+$/, t("Licenses.bms.assignDialog.licenseKeyInvalidCharactersError"))
                      .required(t("Licenses.bms.assignDialog.licenseKeyRequiredError"))
                : string().max(MAX_KEY_LENGTH, (params) =>
                      t("Licenses.bms.assignDialog.licenseKeyMaxError", { max: params.max })
                  ),
            hours: props.fromBms
                ? number()
                      .min(1, (params) => t("Licenses.bms.assignDialog.usageHoursMinError", { min: params.min }))
                      .max(5 * 365 * 24, (params) =>
                          t("Licenses.bms.assignDialog.usageHoursMaxError", { max: params.max })
                      )
                      .required(t("Licenses.bms.assignDialog.usageHoursRequiredError"))
                : number(),
            assignCount: props.fromBms
                ? number()
                      .min(getMinimumAssignCount(), (params) =>
                          t("Licenses.bms.assignDialog.assignMinError", { min: params.min })
                      )
                      .when("keyCount", (keyCount: number, schema: NumberSchema<number>) =>
                          schema.max(Math.trunc(summaryLabelParameter.availableLicenses / keyCount), (params) =>
                              t("Licenses.bms.assignDialog.assignMaxError", { max: params.max })
                          )
                      )
                      .required(t("Licenses.bms.assignDialog.assignRequiredError"))
                : number(),
            startDate: createStartDateSchema(t, minimumStartDate, summaryLabelParameter.expiration),
            expirationDate: createExpirationDateSchema(t, summaryLabelParameter.expiration),
        });
    }, [summaryLabelParameter, t]);

    const licenseKeyTitle = props.isEdit ? t("Licenses.bms.editLicenseKeyTitle") : t("Licenses.bms.assignKeys");

    return (
        <>
            <SubpageLayout
                loading={state.phase === Phase.LOADING_AVAILABLE_LICENSES}
                visible={true}
                title={licenseKeyTitle}
                buttons={
                    <Button
                        disabled={licenseSelectionErrors}
                        variant={"PRIMARY"}
                        type="submit"
                        form="manageLicenseKey"
                        className={classNames(buttons.primaryButton, buttons.medium, buttons.buttonWithoutIcon)}
                        testId={
                            props.fromBms
                                ? testIds.workArea.license.bms.assignKeysDialog.submitButton
                                : testIds.workArea.license.serialNumber.assignKeysView.submitButton
                        }
                    >
                        {t("Licenses.bms.assignDialog.assignButton")}
                    </Button>
                }
            >
                <div>
                    {!props.isEdit && (
                        <div className={formStyle.banner}>
                            {summaryLabelParameter.availableLicenses > 0
                                ? t("Licenses.bms.assignDialog.summaryText", {
                                      availableLicenses: summaryLabelParameter.availableLicenses,
                                      licenseType: summaryLabelParameter.licenseName,
                                      expiration: createDateLabel(summaryLabelParameter.expiration.substring(0, 10)),
                                  })
                                : t("Licenses.bms.assignDialog.noLicensesAvailable")}
                        </div>
                    )}

                    <Formik
                        innerRef={formikRef}
                        initialValues={state.formValues}
                        onSubmit={(values, helpers) => {
                            submit(values, helpers);
                        }}
                        validationSchema={schema}
                        validateOnChange={false}
                        validateOnBlur={false}
                    >
                        {(formikProps: FormikProps<LicenseKeyAssignment>) => {
                            return (
                                <FormikForm onSubmit={formikProps.handleSubmit} id="manageLicenseKey">
                                    {manageFormSections().map((section, index) =>
                                        createFormSection(t, section, "" + index, formikProps)
                                    )}
                                    {availableLicenses && (
                                        <LicenseKeySelectionForm
                                            fromBms={props.fromBms}
                                            isEdit={props.isEdit}
                                            availableLicenses={availableLicenses}
                                            selectedLicense={selectedLicense}
                                            setSelectedLicense={(licenses) => {
                                                setLicenseType(licenses.licenseType);
                                                formikProps
                                                    .setValues({
                                                        ...formikProps.values,
                                                        licenseType: licenses.licenseType,
                                                        assignCount: licenses.licensesToAdd ?? 0,
                                                        available: licenses.parentAvailableAmount,
                                                    })
                                                    .then(() => formikProps.handleBlur);
                                                return setSelectedLicense(licenses);
                                            }}
                                            setLicenseSelectionErrors={setLicenseSelectionErrors}
                                        />
                                    )}
                                </FormikForm>
                            );
                        }}
                    </Formik>
                </div>
            </SubpageLayout>
            <Modal isOpen={resultVisible} hideModal={hideModel} modalTitle={licenseKeyTitle}>
                {createModalBody()}
            </Modal>
            <Modal isOpen={successResultVisible} hideModal={hideResult} modalTitle={licenseKeyTitle}>
                <div className={formStyle.deleteContainer}>
                    <div>{t("Licenses.bms.successMessage")}</div>
                </div>
                <div className={formStyle.buttonContainer}>
                    <button
                        className={buttonStyle.primaryButtonWithoutIcon}
                        type="button"
                        onClick={hideResult}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
        </>
    );
}

export default connector(ManageLicenseKeyView);
